const BASE_URL = 'https://pet-cdn.wxmini.club/music';

export const sounds = {
  pointsUp: [`${BASE_URL}/jiafen.mp3`, `${BASE_URL}/jiafen.WAV`],
  pointsDown: [`${BASE_URL}/jianfen.mp3`, `${BASE_URL}/jianfen.WAV`],
  levelUp: [`${BASE_URL}/shengji.mp3`, `${BASE_URL}/shengji.WAV`],
  maxLevel: [`${BASE_URL}/manji.mp3`, `${BASE_URL}/manji.WAV`],
  levelDown: [`${BASE_URL}/jiangji.mp3`, `${BASE_URL}/jiangji.WAV`],
} as const;

let isEnabled = false;

export const initSoundSettings = () => {
  return isEnabled;
};

export const setSoundEnabled = (enabled: boolean) => {
  isEnabled = enabled;
};

export const getSoundEnabled = () => isEnabled;

const audioElements: Record<string, HTMLAudioElement> = {};
const audioElementUrls: Record<string, string> = {};
const soundUrlIndex: Record<string, number> = {};

const canPlayMp3 = (() => {
  try {
    const audio = new Audio();
    return typeof audio.canPlayType === 'function' && audio.canPlayType('audio/mpeg') !== '';
  } catch {
    return false;
  }
})();

const waitForPlayable = (audio: HTMLAudioElement) => {
  if (audio.readyState >= 2) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const onCanPlay = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error('audio_error'));
    };
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error('audio_timeout'));
    }, 3000);
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      audio.removeEventListener('canplaythrough', onCanPlay);
      audio.removeEventListener('error', onError);
    };
    audio.addEventListener('canplaythrough', onCanPlay, { once: true });
    audio.addEventListener('error', onError, { once: true });
  });
};

const getCandidates = (soundName: keyof typeof sounds) => {
  const urls = sounds[soundName];
  const candidates = canPlayMp3 ? [...urls] : urls.filter(u => !u.toLowerCase().endsWith('.mp3'));
  const startIndex = Math.max(0, Math.min(soundUrlIndex[soundName] ?? 0, candidates.length - 1));
  return { candidates, startIndex };
};

const getOrCreateAudio = (soundName: keyof typeof sounds) => {
  let audio = audioElements[soundName];
  if (!audio) {
    audio = new Audio();
    audio.preload = 'none';
    audioElements[soundName] = audio;
  }
  return audio;
};

export const warmupSounds = async (soundNames?: Array<keyof typeof sounds>) => {
  if (!isEnabled) return;
  const names = soundNames && soundNames.length ? soundNames : (Object.keys(sounds) as Array<keyof typeof sounds>);
  const tasks = names.map(async (name) => {
    const audio = getOrCreateAudio(name);
    const { candidates, startIndex } = getCandidates(name);
    for (let i = startIndex; i < candidates.length; i++) {
      const url = candidates[i];
      if (!url) continue;
      if (audioElementUrls[name] === url && audio.readyState >= 2) {
        soundUrlIndex[name] = i;
        return;
      }
      if (audioElementUrls[name] !== url) {
        audio.src = url;
        audioElementUrls[name] = url;
      }
      try {
        audio.load();
        await waitForPlayable(audio);
        soundUrlIndex[name] = i;
        return;
      } catch {
        continue;
      }
    }
  });
  await Promise.all(tasks);
};

export const preloadSounds = () => {
  return;
};

export const playSound = async (soundName: keyof typeof sounds) => {
  if (!isEnabled) return;
  if (!sounds[soundName]?.length) return;

  try {
    const audio = getOrCreateAudio(soundName);
    const { candidates, startIndex } = getCandidates(soundName);
    for (let i = startIndex; i < candidates.length; i++) {
      const url = candidates[i];
      if (!url) continue;
      if (audioElementUrls[soundName] !== url) {
        audio.src = url;
        audioElementUrls[soundName] = url;
      }
      audio.load();
      await waitForPlayable(audio);
      audio.currentTime = 0;
      await audio.play();
      soundUrlIndex[soundName] = i;
      return;
    }
  } catch {
    // Autoplay policy or other error
    // console.error('Audio playback failed', e);
  }
};
